import { useInput } from "ra-core";
import { ReferenceInput } from "@/components/admin/reference-input";
import { DateInput } from "@/components/admin/date-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { Separator } from "@/components/ui/separator";
import { AutocompleteCompanyInput } from "../companies/AutocompleteCompanyInput.tsx";
import { LocationAutocomplete } from "./LocationAutocomplete";

const LocationInput = ({ source, label }: { source: string; label: string }) => {
  const { field } = useInput({ source });
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium">{label}</label>
      <LocationAutocomplete
        value={field.value ?? ""}
        onChange={field.onChange}
        placeholder={label}
      />
    </div>
  );
};

export const SHIPMENT_METHOD_CHOICES = [
  { id: "3 - 53 ft. Truck", name: "3 - 53 ft. Truck" },
  { id: "1 - PUP Truck", name: "1 - PUP Truck" },
  { id: "2 - 48 ft. Truck", name: "2 - 48 ft. Truck" },
  { id: "4 - Flatbed", name: "4 - Flatbed" },
  { id: "5 - 20 ft Container", name: "5 - 20 ft Container" },
  { id: "6 - 40 ft. Standard Container", name: "6 - 40 ft. Standard Container" },
  { id: "7 - 40 ft. High Cube Container", name: "7 - 40 ft. High Cube Container" },
  { id: "8 - 45 ft. High Cube Container", name: "8 - 45 ft. High Cube Container" },
];

export const FREIGHT_TERMS_CHOICES = [
  { id: "EXW", name: "EXW" },
  { id: "CPT", name: "CPT" },
  { id: "1 - Delivered", name: "1 - Delivered" },
  { id: "2 - EXW", name: "2 - EXW" },
  { id: "3 - Plus Freight", name: "3 - Plus Freight" },
];

export const LOAD_TYPE_CHOICES = [
  { id: "Standard", name: "Standard" },
  { id: "Admin", name: "Admin" },
  { id: "WDS", name: "WDS" },
  { id: "Inventory", name: "Inventory" },
  { id: "UCD", name: "UCD" },
];

export const LOAD_STATUS_CHOICES = [
  { id: "pending_so_approval", name: "Pending SO Approval" },
  { id: "closed_won_pca", name: "Closed Won PCA" },
  { id: "closed_won_ppoa", name: "Closed Won PPOA" },
  { id: "lg_booking", name: "LG Booking" },
  { id: "lg_booked", name: "LG Booked" },
  { id: "lg_picked_up", name: "LG Picked Up" },
  { id: "ready_to_invoice", name: "Ready to Invoice" },
  { id: "invoiced", name: "Invoiced" },
  { id: "completed", name: "Completed" },
  { id: "pca_hold", name: "PCA Hold" },
  { id: "logistics_hold", name: "Logistics Hold" },
  { id: "sos", name: "SOS" },
  { id: "closed_lost", name: "Closed Lost" },
];

export const DOCUMENT_TYPE_CHOICES = [
  { id: "ar_note", name: "AR Note" },
  { id: "bol", name: "Bill of Lading" },
  { id: "booking_confirmation", name: "Booking Confirmation" },
  { id: "booking_request", name: "Booking Request" },
  { id: "booking_sheet", name: "Booking Sheet" },
  { id: "bv_credit_memo", name: "BV Credit Memo" },
  { id: "bv_invoice", name: "BV Invoice" },
  { id: "credit_request", name: "Credit Request Form" },
  { id: "customs_invoice", name: "Customs Invoice" },
  { id: "email", name: "E-mail" },
  { id: "freight_invoice", name: "Freight Invoice" },
  { id: "freight_quote", name: "Freight Quote" },
  { id: "freight_request", name: "Freight Request" },
  { id: "loading_doc", name: "Loading Document" },
  { id: "nsa", name: "NSA" },
  { id: "other", name: "Other" },
  { id: "packing_list", name: "Packing List" },
  { id: "pca_note", name: "PCA Note" },
  { id: "pickup_confirmation", name: "Pick-up Confirmation" },
  { id: "po_signed", name: "PO Signed" },
  { id: "Proforma", name: "Proforma" },
  { id: "purchase_order", name: "Purchase Order" },
  { id: "sales_order", name: "Sales Order" },
  { id: "scale_tickets", name: "Scale Tickets" },
  { id: "so_signed", name: "SO Signed" },
  { id: "vend_invoice", name: "Vendor Bill" },
  { id: "vendor_credit_memo", name: "Vendor Credit Memo" },
  { id: "wire_transfer_request", name: "Wire Transfer Request" },
  { id: "gg_vend_packing", name: "Graded Goods Vendor Packing List" },
  { id: "gg_packing_list", name: "Graded Goods Packing List" },
];

export const TERMS_CHOICES = [
  { id: "Prepay", name: "Prepay" },
  { id: "Prepay or Offsets", name: "Prepay or Offsets" },
  { id: "Pay Upon Loading", name: "Pay Upon Loading" },
  { id: "Due on Receipt", name: "Due on Receipt" },
  { id: "50-50 Prepay - 20 Days", name: "50-50 Prepay - 20 Days" },
  { id: "50-50 Prepay - Upon Loading", name: "50-50 Prepay - Upon Loading" },
  { id: "50-50 Prepay - Upon Invoice", name: "50-50 Prepay - Upon Invoice" },
  { id: "75 Prepay - 25 Pay Upon Loading", name: "75 Prepay - 25 Pay Upon Loading" },
  { id: "90-10 Prepay - Upon Loading", name: "90-10 Prepay - Upon Loading" },
  { id: "80-20 Prepay - Upon Loading", name: "80-20 Prepay - Upon Loading" },
  { id: "50-50 Prepay - 15 Days", name: "50-50 Prepay - 15 Days" },
  { id: "40-60 Prepay - 20 Days", name: "40-60 Prepay - 20 Days" },
  { id: "30-70 Prepay - 20 Days", name: "30-70 Prepay - 20 Days" },
  { id: "15-85 Prepay - 20 Days", name: "15-85 Prepay - 20 Days" },
  { id: "50-50 Prepay - 30 Days", name: "50-50 Prepay - 30 Days" },
  { id: "40-60 Prepay - 30 Days", name: "40-60 Prepay - 30 Days" },
  { id: "30-70 Prepay - 30 Days", name: "30-70 Prepay - 30 Days" },
  { id: "15-85 Prepay - 30 Days", name: "15-85 Prepay - 30 Days" },
  { id: "7 Days", name: "7 Days" },
  { id: "10 Days", name: "10 Days" },
  { id: "15 Days", name: "15 Days" },
  { id: "20 Days", name: "20 Days" },
  { id: "25 Days", name: "25 Days" },
  { id: "30 Days", name: "30 Days" },
  { id: "45 Days", name: "45 Days" },
  { id: "60 Days", name: "60 Days" },
  { id: "Due_on_receipt", name: "Due on receipt (Do not use)" },
];

export const PAYMENT_METHOD_CHOICES = [
  { id: "Wire", name: "Wire" },
  { id: "Cheque", name: "Cheque" },
  { id: "ACH", name: "ACH" },
  { id: "Interac", name: "Interac" },
];

export const LoadInputs = () => {
  return (
    <div className="flex flex-col gap-8">
      <LoadStatusInputs />
      <Separator />
      <LoadCompanyInputs />
      <Separator />
      <LoadOwnerInputs />
      <Separator />
      <LoadFreightInputs />
      <Separator />
      <LoadFinancialInputs />
      <Separator />
      <LoadNotesInputs />
    </div>
  );
};

const LoadCompanyInputs = () => {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-medium">Companies</h3>
      <div className="grid grid-cols-2 gap-4">
        <ReferenceInput source="vendor_company_id" reference="companies">
          <AutocompleteCompanyInput
            label="resources.loads.fields.vendor_company_id"
            modal
          />
        </ReferenceInput>

        <ReferenceInput source="customer_company_id" reference="companies">
          <AutocompleteCompanyInput
            label="resources.loads.fields.customer_company_id"
            modal
          />
        </ReferenceInput>

        <ReferenceInput source="vendor_billing_company_id" reference="companies">
          <AutocompleteCompanyInput
            label="resources.loads.fields.vendor_billing_company_id"
            modal
          />
        </ReferenceInput>

        <ReferenceInput source="customer_billing_company_id" reference="companies">
          <AutocompleteCompanyInput
            label="resources.loads.fields.customer_billing_company_id"
            modal
          />
        </ReferenceInput>

        <ReferenceInput source="pickup_company_id" reference="companies">
          <AutocompleteCompanyInput
            label="resources.loads.fields.pickup_company_id"
            modal
          />
        </ReferenceInput>

        <ReferenceInput source="delivery_company_id" reference="companies">
          <AutocompleteCompanyInput
            label="resources.loads.fields.delivery_company_id"
            modal
          />
        </ReferenceInput>

        <ReferenceInput source="pickup_account_id" reference="companies">
          <AutocompleteCompanyInput
            label="resources.loads.fields.pickup_account_id"
            modal
          />
        </ReferenceInput>

        <ReferenceInput source="delivery_account_id" reference="companies">
          <AutocompleteCompanyInput
            label="resources.loads.fields.delivery_account_id"
            modal
          />
        </ReferenceInput>
      </div>
    </div>
  );
};

const LoadStatusInputs = () => {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-medium">Status & Dates</h3>
      <div className="grid grid-cols-2 gap-4">
        <SelectInput
          source="status"
          label="resources.loads.fields.status"
          choices={LOAD_STATUS_CHOICES}
          optionText="name"
          optionValue="id"
          defaultValue="pending_so_approval"
          helperText={false}
        />

        <SelectInput
          source="load_type"
          label="resources.loads.fields.load_type"
          choices={LOAD_TYPE_CHOICES}
          optionText="name"
          optionValue="id"
          defaultValue="Standard"
          helperText={false}
        />

        <DateInput
          source="pickup_date"
          label="resources.loads.fields.pickup_date"
          helperText={false}
        />
        </div>
        <div className="grid grid-cols-2 gap-4">

        <TextInput
          source="vendor_reference_number"
          label="resources.loads.fields.vendor_reference_number"
          helperText={false}
        />

        <TextInput
          source="customer_reference_number"
          label="resources.loads.fields.customer_reference_number"
          helperText={false}
        />
      </div>

    </div>
  );
};

const LoadFreightInputs = () => {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-medium">Freight Details</h3>
      <div className="grid grid-cols-2 gap-4">
        <SelectInput
          source="shipment_method"
          label="resources.loads.fields.shipment_method"
          choices={SHIPMENT_METHOD_CHOICES}
          optionText="name"
          optionValue="id"
          helperText={false}
        />
        <SelectInput
          source="freight_terms"
          label="resources.loads.fields.freight_terms"
          choices={FREIGHT_TERMS_CHOICES}
          optionText="name"
          optionValue="id"
          helperText={false}
        />
        <LocationInput source="origin_location" label="Origin" />
        <LocationInput source="destination_location" label="Destination" />
        <TextInput
          source="container_number"
          label="resources.loads.fields.container_number"
          helperText={false}
        />
        <TextInput
          source="seal_number"
          label="resources.loads.fields.seal_number"
          helperText={false}
        />
        <TextInput
          source="booking_number"
          label="resources.loads.fields.booking_number"
          helperText={false}
        />
        <BooleanInput
          source="booking_requested"
          label="resources.loads.fields.booking_requested"
          helperText={false}
        />
        <BooleanInput
          source="bolt_seal_required"
          label="resources.loads.fields.bolt_seal_required"
          helperText={false}
        />
      </div>
    </div>
  );
};

const LoadOwnerInputs = () => {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-medium">Owners</h3>
      <div className="grid grid-cols-2 gap-4">
        <ReferenceInput
          source="sales_owner_id"
          reference="sales"
          filter={{ "disabled@neq": true }}
        >
          <AutocompleteInput
            label="resources.loads.fields.sales_owner_id"
            optionText={(record) => `${record.first_name} ${record.last_name}`}
            helperText={false}
          />
        </ReferenceInput>

        <ReferenceInput
          source="purchasing_owner_id"
          reference="sales"
          filter={{ "disabled@neq": true }}
        >
          <AutocompleteInput
            label="resources.loads.fields.purchasing_owner_id"
            optionText={(record) => `${record.first_name} ${record.last_name}`}
            helperText={false}
          />
        </ReferenceInput>

        <ReferenceInput
          source="logistics_owner_id"
          reference="sales"
          filter={{ "disabled@neq": true }}
        >
          <AutocompleteInput
            label="resources.loads.fields.logistics_owner_id"
            optionText={(record) => `${record.first_name} ${record.last_name}`}
            helperText={false}
          />
        </ReferenceInput>

        <ReferenceInput
          source="invoiced_by_user_id"
          reference="sales"
          filter={{ "disabled@neq": true }}
        >
          <AutocompleteInput
            label="resources.loads.fields.invoiced_by_user_id"
            optionText={(record) => `${record.first_name} ${record.last_name}`}
            helperText={false}
          />
        </ReferenceInput>
      </div>
    </div>
  );
};

const CURRENCY_CHOICES = [
  { id: "CAD", name: "CAD" },
  { id: "USD", name: "USD" },
];

const LoadFinancialInputs = () => {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-medium">Financials</h3>
      <div className="grid grid-cols-2 gap-4">
        <SelectInput
          source="vendor_currency"
          label="resources.loads.fields.vendor_currency"
          choices={CURRENCY_CHOICES}
          optionText="name"
          optionValue="id"
          defaultValue="USD"
          helperText={false}
        />
        <SelectInput
          source="customer_currency"
          label="resources.loads.fields.customer_currency"
          choices={CURRENCY_CHOICES}
          optionText="name"
          optionValue="id"
          defaultValue="USD"
          helperText={false}
        />
        <TextInput
          source="vendor_invoice_total"
          label="resources.loads.fields.vendor_invoice_total"
          helperText={false}
        />
        <TextInput
          source="customer_invoice_total"
          label="resources.loads.fields.customer_invoice_total"
          helperText={false}
        />
        <SelectInput
          source="vendor_payment_method"
          label="resources.loads.fields.vendor_payment_method"
          choices={PAYMENT_METHOD_CHOICES}
          optionText="name"
          optionValue="id"
          helperText={false}
        />
        </div>
        <div className="grid grid-cols-2 gap-4">
        <SelectInput
          source="vendor_terms"
          label="resources.loads.fields.vendor_terms"
          choices={TERMS_CHOICES}
          optionText="name"
          optionValue="id"
          helperText={false}
        />
        <SelectInput
          source="customer_terms"
          label="resources.loads.fields.customer_terms"
          choices={TERMS_CHOICES}
          optionText="name"
          optionValue="id"
          helperText={false}
        />
        <DateInput
          source="vendor_date_paid"
          label="resources.loads.fields.vendor_date_paid"
          helperText={false}
        />
        <DateInput
          source="customer_date_paid"
          label="resources.loads.fields.customer_date_paid"
          helperText={false}
        />
        <div className="col-span-2">
          <BooleanInput
            source="zero_rated"
            label="resources.loads.fields.zero_rated"
            helperText={false}
          />
        </div>
      </div>
    </div>
  );
};

const LoadNotesInputs = () => {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-medium">Notes</h3>
      <TextInput
        source="supplier_requirements"
        label="resources.loads.fields.supplier_requirements"
        multiline
        helperText={false}
      />
      <TextInput
        source="customer_requirements"
        label="resources.loads.fields.customer_requirements"
        multiline
        helperText={false}
      />
      <TextInput
        source="internal_notes"
        label="resources.loads.fields.internal_notes"
        multiline
        helperText={false}
      />
      <TextInput
        source="notes_for_po"
        label="resources.loads.fields.notes_for_po"
        multiline
        helperText={false}
      />
      <TextInput
        source="notes_for_freight_forwarder"
        label="resources.loads.fields.notes_for_freight_forwarder"
        multiline
        helperText={false}
      />
    </div>
  );
};
